import store from '../store';
import endpoint from '../util/endpoint';
import { clearImages, setCashierGuid, updatePayment } from './checkout.action';
import memoize from '@whiz-cart/node-shared/memoize';
import { CheckoutPayment } from '@whiz-cart/node-shared/models/checkout/payment';
import { LiveSession } from '@whiz-cart/node-shared/models/dsi/liveSession';
import type { Payment } from '@whiz-cart/node-shared/models/payment/payment';
import { VerificationItem } from '@whiz-cart/node-shared/models/payment/verificationItem';
import { UiProduct } from '@whiz-cart/node-shared/models/product/uiProduct';
import { Session } from '@whiz-cart/node-shared/models/session/session';
import { service } from '@whiz-cart/node-shared/service/service';
import _ from 'lodash';
import { Action } from 'schummar-state/react';

export interface PaymentItem extends Payment {
    storeGuid: string;
    isDemoModeOn?: boolean;
    approved?: boolean;
    posProcessingType?: 'deferred';
    updatedOn?: Date;
}

class CheckoutService {
    transformIncomingPayment(
        {
            paymentRequest,
            deviceName,
            isMobile,
            loginHash,
            endedOn,
            paymentRequestGuid,
            paymentMethod,
            sessionGuid,
            total,
            processingBy,
            postProcessingBy,
            parkingEan,
            isDemoModeOn,
            isSuspicious,
            posRegistrationCompletedOn,
            completionSource,
            deviceMetadata,
            externalPayment,
            loginType,
            userGuid,
            customer,
        }: CheckoutPayment,
        storeGuid: string,
    ) {
        const _deviceName = deviceName ?? paymentRequest?.cashierId ?? '';

        return {
            paymentRequestGuid,
            paymentMethod,
            sessionGuid,
            total,
            processingBy,
            postProcessingBy,
            endedOn,
            deviceName: isMobile ? `E${_deviceName.padStart(3, '0')}` : _deviceName,
            storeGuid,
            isMobile,
            loginHash,
            parkingEan: { code: parkingEan, ...parkingEan },
            isDemoModeOn,
            isBookmarked: isSuspicious,
            posRegistrationCompletedOn,
            completionSource,
            deviceMetadata,
            externalPayment,
            loginType,
            userGuid,
            customer,
            ...paymentRequest,
        };
    }

    addBasketInfo(payment: Payment, basket: Session['items']) {
        const groupedBasket = basket && _.keyBy(basket, 'basketItemGuid');

        const paymentCodes = payment.paymentCodes?.map((code) => {
            const [, , sessionCode] = code.code?.match(/(XE|XG)(92\d*)X/) ?? [];
            const [, , customerCard] = code.code?.match(/(XE|XG)(995\d{10})X/) ?? [];
            const [, , employeeCard] = code.code?.match(/(XE|XG)(999\d{10}|636453\d*)X/) ?? [];
            const items =
                groupedBasket &&
                _.map(code.basketItemsCounts, (amount, basketItemGuid) => ({ ...groupedBasket[basketItemGuid], amount: amount }));
            const total = !items?.length
                ? undefined
                : _.sumBy(items, ({ basketItemGuid, amount }) => {
                      const { totalPrice = 0 } = groupedBasket?.[basketItemGuid ?? ''] ?? {};
                      return amount * (totalPrice ?? 0);
                  });

            return { ...code, sessionCode, customerCard, employeeCard, total, items };
        });

        const parkingEan = {
            ...payment.parkingEan,
            customerCard: payment.customerCard,
            employeeCard: payment.employeeCard,
            total: payment.total,
            items: groupedBasket && _.values(groupedBasket).filter((i) => i.amount > 0),
        };

        return {
            ...payment,
            parkingEan,
            paymentCodes,
            basket: groupedBasket,
        };
    }

    getPendingRequests(storeGuid: string) {
        return endpoint('cashier.getPendingRequests', { storeGuid }).get({
            transform: (payments) => (payments as CheckoutPayment[]).map((data) => this.transformIncomingPayment(data, storeGuid)),
        });
    }

    getPaymentHistory(
        storeGuid: string,
        skip = 0,
        limit = 10,
        {
            from = null,
            to = null,
            cartId = null,
            isExpress = null,
            isQrPayment = null,
            paymentMethods = [],
            userGuid = null,
            isBookmarked = null,
            noPayment = null,
            pos = undefined,
        },
        sessionGuid: string | null = null,
        minTotal: number | undefined,
        maxTotal: number | undefined,
        parkingEan: string,
        receiptNo: string,
    ) {
        return endpoint('storeManager.getPaymentHistory', { storeGuid, skip, limit }).get({
            params: {
                from,
                to,
                cartId,
                sessionGuid,
                minTotal,
                maxTotal,
                isExpress: paymentMethods.length === 0 && !isExpress ? undefined : isExpress,
                paymentMethods: paymentMethods.length === 0 && !isExpress ? undefined : paymentMethods,
                bookmarkedSessions: isBookmarked || undefined,
                includeUnpaidSessions: noPayment || undefined,
                isQrPayment: isQrPayment || undefined,
                parkingEan,
                userGuid,
                receiptNo,
                includeExternalPayments: true,
                pos,
            },
            transform: (response) => ({
                items: ((response as any).items as CheckoutPayment[]).map((data) => this.transformIncomingPayment(data, storeGuid)),
                countPerDay: (response as any).countPerDay as Record<string, number>,
            }),
        });
    }

    async _updatePayment(endpointName: string | undefined, payment: Payment, data: Record<string, unknown>) {
        const { sessionGuid, paymentRequestGuid, storeGuid, cashierGuid } = payment;
        const source = 'storeManager';
        store.dispatch(updatePayment(payment));

        if (endpointName) {
            await endpoint(endpointName as any, { storeGuid } as any).post({
                sessionGuid,
                paymentRequestGuid,
                cashierGuid,
                source,
                ...data,
            });
        }
    }

    abortPayment(payment: Payment, cashierGuid: string) {
        return this._updatePayment(
            'cancelPayment',
            { ...payment, status: 4 },
            { cashierGuid: cashierGuid === 'hidePictures' ? '' : cashierGuid },
        );
    }

    confirmPayment(payment: Payment, cashierGuid: string) {
        return this._updatePayment(
            'completePayment',
            { ...payment, status: 2, posRegistrationCompletedOn: true },
            { cashierGuid: cashierGuid === 'hidePictures' ? '' : cashierGuid },
        );
    }

    confirmVerificationItem(payment: Payment, item: VerificationItem) {
        item = { ...item, approved: true };
        const verificationItems = payment.verificationItems
            ?.filter((i) =>
                i.verificationItemGuid
                    ? i.verificationItemGuid !== item.verificationItemGuid
                    : i.verificationType !== item.verificationType,
            )
            .concat(item);
        const allApproved = verificationItems?.every((i) => i.approved);

        return this._updatePayment(
            item.verificationItemGuid ? 'verifyPaymentVerificationItem' : undefined,
            { ...payment, verificationItems, status: allApproved ? 1 : payment.status },
            { verificationGuid: item.verificationItemGuid, verificationItemGuid: item.verificationItemGuid, approved: true },
        );
    }

    lockPayment(payment: Payment, cashierGuid: string) {
        return this._updatePayment(payment.isPosProcessing ? 'posProcessingLock' : 'lock', payment, {
            cashierGuid: cashierGuid === 'hidePictures' ? '' : cashierGuid,
        });
    }

    unlockPayment(payment: Payment, cashierGuid: string) {
        return this._updatePayment(payment.isPosProcessing ? 'posProcessingUnlock' : 'unlock', payment, {
            cashierGuid: cashierGuid === 'hidePictures' ? '' : cashierGuid,
        });
    }

    async loadPayment(storeGuid: string, sessionGuid: string, paymentRequestGuid: string) {
        try {
            const data = (await endpoint('cashier.getPayment', { storeGuid, sessionGuid, paymentRequestGuid }).get()) as any;
            return this.transformIncomingPayment(data, storeGuid);
        } catch (e: any) {
            if (e.response?.status !== 404) {
                console.error('Failed to get payment:', e.message);
                throw e;
            }
            return null;
        }
    }

    @(memoize() as any)
    async loadBasket(storeGuid: string, sessionGuid: string, paymentRequestGuid: string) {
        try {
            return await endpoint('cashier.getPaymentBasket', { storeGuid, sessionGuid, paymentRequestGuid }).get();
        } catch (e: any) {
            if (e.response?.status !== 404) {
                console.error('Failed to get basket:', e.message);
                throw e;
            }
        }
    }

    loadSession = new Action(async ({ storeGuid, sessionGuid }: { storeGuid: string; sessionGuid: string }) => {
        try {
            return (await endpoint('cashier.getSession', { storeGuid, sessionGuid }).get()) as LiveSession;
        } catch (e: any) {
            if (e.response?.status !== 404) {
                console.error('Failed to get session:', e.message);
                throw e;
            }
        }
    });

    async loadExpressCheckoutList(storeGuid: string) {
        try {
            const list = (await endpoint('cashier.expressCheckout', { storeGuid }).get()) as any[];
            if (list && list?.length === 0) {
                this.updateExpressCheckoutRegister({ cashierGuid: 'hidePictures', cashierId: '' });
            }
            return list;
        } catch (e: any) {
            if (e.response?.status !== 404) {
                console.error('Failed to get list:', e.message);
                throw e;
            }
        }
    }

    updateExpressCheckoutRegister({ cashierGuid, cashierId }: { cashierGuid: string; cashierId: string }) {
        localStorage.setItem('localCashier', cashierGuid === 'hidePictures' ? 'hidePictures' : cashierId);
        store.dispatch(setCashierGuid(cashierGuid));
    }

    clearExpressImages() {
        store.dispatch(clearImages());
    }

    async getSessionByReceiptNumber(storeGuid: string, receiptNumber: string) {
        try {
            return await endpoint('storeManager.getSessionByReceiptNumber', { storeGuid, receiptNumber }).get<{
                sessionGuid: string;
                paymentRequestGuid?: string;
            }>();
        } catch (e) {
            if ((e as any).data.status === 110) return null;
            throw e;
        }
    }

    getProductInformation = new Action(async ({ storeGuid, barcode }: { storeGuid?: string; barcode: string }) => {
        if (!storeGuid) {
            return null;
        }
        const result = await endpoint('cashier.getProductInformation', { storeGuid }).get({ params: { storeGuid, barcode } });
        return result as UiProduct;
    });
}

export default service('checkoutService', CheckoutService);

export type MposVersion = 'mposv1' | 'mposv2' | null;
